import React, { useState } from "react"
import { TouchableOpacity, View } from "react-native"
import { ProgressBar } from "react-native-paper"
import { theme } from "@common/theme/theme"
import TextM from "@common/components/text/TextM"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import { IconImageM } from "@common/components/icons/IconsM"
import moment from "moment"
import "moment/locale/fr"
import { SchoolAssessmentDetailModal } from "./SchoolAssessmentDetailModal"

moment.locale("fr")

const urgencyColors = {
    low: theme.colors.success,
    medium: theme.colors.warning,
    high: theme.colors.warningDark || "#FF9800",
    critical: theme.colors.error,
}

const getStatusFromProgress = (progress) => {
    if (progress === 0)
        return { label: "Non commencé", color: theme.colors.error }
    if (progress >= 100) return { label: "Prêt", color: theme.colors.success }
    return { label: "En préparation", color: theme.colors.warning }
}

const SchoolAssessmentCard = ({
    assessment,
    onEdit,
    onDelete,
}) => {
    const [expanded, setExpanded] = useState(false)
    const [detailsModalVisible, setDetailsModalVisible] = useState(false)

    const {
        title,
        date,
        school_subject,
        preparation_progress,
        chapters_progress,
        days_until,
        urgency,
        key_requirements,
    } = assessment

    const displayDate = moment(date).format("D MMM")
    const daysLabel = days_until === 0 ? "Aujourd'hui" : `J-${days_until}`
    const urgencyColor = urgencyColors[urgency] || theme.colors.grey
    const { label: statusLabel, color: statusColor } =
        getStatusFromProgress(preparation_progress)

    const subjectName = school_subject?.name || "Matière"
    const subjectColor = school_subject?.color || theme.colors.primary
    const subjectIcon = school_subject?.icon
    const displayTitle = title || `Contrôle ${subjectName}`

    return (
        <TouchableOpacity
            onPress={() => setExpanded(!expanded)}
            activeOpacity={0.8}
            style={{
                backgroundColor: theme.colors.white,
                borderRadius: 12,
                borderWidth: 1,
                borderLeftWidth: 4,
                borderLeftColor: subjectColor,
                borderColor: theme.colors.lightMore,
                marginBottom: 12,
                overflow: "hidden",
            }}
        >
            {/* Header - always visible */}
            <View style={{ padding: 15 }}>
                <View
                    style={{
                        flexDirection: "row",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                    }}
                >
                    <View
                        style={{
                            flex: 1,
                            flexDirection: "row",
                            alignItems: "flex-start",
                        }}
                    >
                        {subjectIcon && (
                            <IconImageM
                                uri={subjectIcon}
                                width={32}
                                style={{ marginRight: 10 }}
                            />
                        )}
                        <View style={{ flex: 1 }}>
                            <TextM fontSize={16} fontWeight="Bold" wrap>
                                {displayTitle}
                            </TextM>
                            <View
                                style={{
                                    flexDirection: "row",
                                    alignItems: "center",
                                    marginTop: 4,
                                    flexWrap: "wrap",
                                }}
                            >
                                <TextM fontSize={13} color={theme.colors.grey}>
                                    {displayDate}
                                </TextM>
                                <View
                                    style={{
                                        backgroundColor: urgencyColor,
                                        paddingHorizontal: 8,
                                        paddingVertical: 2,
                                        borderRadius: 10,
                                        marginLeft: 8,
                                    }}
                                >
                                    <TextM
                                        fontSize={11}
                                        fontWeight="Bold"
                                        color={theme.colors.white}
                                    >
                                        {daysLabel}
                                    </TextM>
                                </View>
                                <TextM
                                    fontSize={11}
                                    color={statusColor}
                                    style={{ marginLeft: 8 }}
                                >
                                    • {statusLabel}
                                </TextM>
                            </View>
                        </View>
                    </View>

                    <View style={{ alignItems: "flex-end" }}>
                        <View
                            style={{
                                flexDirection: "row",
                                alignItems: "center",
                            }}
                        >
                            <TextM
                                fontSize={18}
                                fontWeight="ExtraBold"
                                color={theme.colors.grey}
                            >
                                {preparation_progress}%
                            </TextM>
                            <FontAwesome5
                                name={expanded ? "chevron-up" : "chevron-down"}
                                size={12}
                                color={theme.colors.grey}
                                style={{ marginLeft: 8 }}
                            />
                        </View>
                    </View>
                </View>

                {/* Progress bar */}
                <View style={{ marginTop: 12 }}>
                    <ProgressBar
                        progress={preparation_progress / 100}
                        color={urgencyColor}
                        style={{
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: theme.colors.lightMore,
                        }}
                    />
                </View>
            </View>

            {/* Expanded content */}
            {expanded && (
                <View
                    style={{
                        borderTopWidth: 1,
                        borderTopColor: theme.colors.lightMore,
                        padding: 15,
                    }}
                >
                    {/* Chapters progress */}
                    {chapters_progress && chapters_progress.length > 0 && (
                        <View>
                            <TextM
                                fontSize={13}
                                fontWeight="Bold"
                                color={theme.colors.grey}
                                style={{ marginBottom: 10 }}
                            >
                                Chapitres :
                            </TextM>
                            {chapters_progress.map((chapter) => {
                                // Use backend-calculated progress (XP-based asymptotic curve)
                                const chapterProgress = chapter.progress ?? 0
                                const progressColor =
                                    chapterProgress >= 70
                                        ? theme.colors.success
                                        : chapterProgress >= 40
                                        ? theme.colors.warning
                                        : theme.colors.error

                                return (
                                    <View
                                        key={chapter.chapter_id}
                                        style={{
                                            flexDirection: "row",
                                            alignItems: "center",
                                            marginBottom: 8,
                                        }}
                                    >
                                        <View style={{ flex: 1 }}>
                                            <TextM fontSize={13} wrap>
                                                {chapter.name}
                                            </TextM>
                                        </View>
                                        <View
                                            style={{
                                                width: 80,
                                                marginHorizontal: 10,
                                            }}
                                        >
                                            <ProgressBar
                                                progress={chapterProgress / 100}
                                                color={progressColor}
                                                style={{
                                                    height: 6,
                                                    borderRadius: 3,
                                                    backgroundColor:
                                                        theme.colors.lightMore,
                                                }}
                                            />
                                        </View>
                                        <TextM
                                            fontSize={12}
                                            fontWeight="Bold"
                                            color={progressColor}
                                            style={{ width: 35 }}
                                        >
                                            {chapterProgress}%
                                        </TextM>
                                    </View>
                                )
                            })}
                        </View>
                    )}

                    {/* Actions */}
                    <View
                        style={{
                            flexDirection: "row",
                            justifyContent: "flex-end",
                            marginTop: 15,
                            borderTopWidth: 1,
                            borderTopColor: theme.colors.lightMore,
                            paddingTop: 15,
                        }}
                    >
                        {key_requirements && (
                            <TouchableOpacity
                                onPress={() => setDetailsModalVisible(true)}
                                style={{
                                    flexDirection: "row",
                                    alignItems: "center",
                                    paddingHorizontal: 15,
                                    paddingVertical: 8,
                                }}
                            >
                                <FontAwesome5
                                    name="info-circle"
                                    size={14}
                                    color={theme.colors.primary}
                                />
                                <TextM
                                    fontSize={13}
                                    color={theme.colors.primary}
                                    style={{ marginLeft: 6 }}
                                >
                                    Détails
                                </TextM>
                            </TouchableOpacity>
                        )}
                        <TouchableOpacity
                            onPress={onEdit}
                            style={{
                                flexDirection: "row",
                                alignItems: "center",
                                paddingHorizontal: 15,
                                paddingVertical: 8,
                            }}
                        >
                            <FontAwesome5
                                name="edit"
                                size={14}
                                color={theme.colors.primary}
                            />
                            <TextM
                                fontSize={13}
                                color={theme.colors.primary}
                                style={{ marginLeft: 6 }}
                            >
                                Modifier
                            </TextM>
                        </TouchableOpacity>
                        <TouchableOpacity
                            onPress={onDelete}
                            style={{
                                flexDirection: "row",
                                alignItems: "center",
                                paddingHorizontal: 15,
                                paddingVertical: 8,
                            }}
                        >
                            <FontAwesome5
                                name="trash"
                                size={14}
                                color={theme.colors.error}
                            />
                            <TextM
                                fontSize={13}
                                color={theme.colors.error}
                                style={{ marginLeft: 6 }}
                            >
                                Supprimer
                            </TextM>
                        </TouchableOpacity>
                    </View>
                </View>
            )}

            {/* Key Requirements Details Modal */}
            <SchoolAssessmentDetailModal
                visible={detailsModalVisible}
                onClose={() => setDetailsModalVisible(false)}
                key_requirements={key_requirements}
            />
        </TouchableOpacity>
    )
}

export default SchoolAssessmentCard
